/**
 * Attribution API
 *
 * Referral/attribution matching. Device fingerprint is collected by the caller
 * (RN uses Device + react-native-localize; web uses navigator APIs); this module
 * just sends the fingerprint to the backend and reports the match decision.
 *
 * The factory takes a single axios client. Callers that need both authed and
 * unauthed calls (e.g., first-launch attribution before login) instantiate the
 * factory twice with different clients and dispatch at call time.
 */

import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  AttributionMatchRequest,
  AttributionMatchResponse,
  AttributionSource,
  ClaimAttributionResponse,
  DeviceFingerprint,
} from '@wg/types';

// ──────────────────────────────────────────────────────────────
// Pure helpers — no http, safe to call anywhere
// ──────────────────────────────────────────────────────────────

/**
 * Normalize a referral code:
 * - Uppercase + trim
 * - For WG- format codes, replace O→0 and I→1 (ambiguity handling)
 * - Strip any character that's not alphanumeric, dash, or underscore
 */
export function normalizeReferralCode(code: string): string {
  let normalized = code.toUpperCase().trim();

  if (normalized.startsWith('WG-')) {
    normalized = normalized.replace(/O/g, '0').replace(/I/g, '1');
  }

  return normalized.replace(/[^A-Z0-9_-]/g, '');
}

/**
 * Validate a referral code's shape. Accepts:
 * - Simple codes: 3+ alphanumeric (TEST001, AFFILIATE_DEMO)
 * - WG format: WG-XXXX-XXXX-XXXX-XXXX
 */
export function isValidReferralCode(code: string): boolean {
  const wgPattern = /^WG-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
  if (wgPattern.test(code)) return true;
  const simplePattern = /^[A-Z0-9][A-Z0-9_-]{2,}$/;
  return simplePattern.test(code);
}

// ──────────────────────────────────────────────────────────────
// Factory
// ──────────────────────────────────────────────────────────────

export interface CheckAttributionInput {
  fingerprint: DeviceFingerprint;
  clickId?: string;
  referralCode?: string;
  source?: AttributionSource;
  sourceDetail?: string;
}

export interface AttributionApi {
  /**
   * Check if this install matches a previous referral click. Backend uses:
   * 1. click_id (100% accurate via Android Install Referrer)
   * 2. referral_code (100% accurate, supports groups)
   * 3. Device fingerprint (70-85%, probabilistic)
   *
   * Always returns; on error returns `{ matched: false, message }` so
   * attribution failures don't block app launch.
   */
  checkAttribution(
    input: CheckAttributionInput,
  ): Promise<AttributionMatchResponse>;

  /**
   * Claim attribution for an authenticated user (call after sign-up/sign-in).
   * Links account ↔ install record so we can track the full funnel:
   * click → install → register → purchase.
   */
  claimAttribution(
    fingerprint: DeviceFingerprint,
    installId?: string,
  ): Promise<ClaimAttributionResponse>;
}

export function createAttributionApi(
  http: AxiosInstance,
  logger: Logger,
): AttributionApi {
  return {
    async checkAttribution({
      fingerprint,
      clickId,
      referralCode,
      source,
      sourceDetail,
    }) {
      try {
        let normalizedCode = referralCode;
        if (normalizedCode) {
          normalizedCode = normalizeReferralCode(normalizedCode);
          if (!isValidReferralCode(normalizedCode)) {
            logger.warn('[AttributionAPI] Invalid referral code format', {
              referralCode: normalizedCode,
            });
            return { matched: false, message: 'Invalid code format' };
          }
        }

        const payload: AttributionMatchRequest = {
          device_fingerprint: fingerprint,
          click_id: clickId,
          referral_code: normalizedCode,
          source,
          source_detail: sourceDetail,
        };

        logger.info('[AttributionAPI] Checking attribution match', {
          hasClickId: !!clickId,
          hasReferralCode: !!normalizedCode,
          referralCode: normalizedCode,
          source,
          platform: fingerprint.platform,
        });

        const response = await http.post<AttributionMatchResponse>(
          '/attribution/v1/match',
          payload,
        );

        if (response.data.matched) {
          logger.info('[AttributionAPI] Attribution matched', {
            matchId: response.data.match_id,
            referralSource: response.data.referral_source?.code,
            type: response.data.referral_source?.type,
          });
        } else {
          logger.info('[AttributionAPI] No attribution match found', {
            message: response.data.message,
          });
        }

        return response.data;
      } catch (error: any) {
        logger.error('[AttributionAPI] Failed to check attribution', {
          error: error?.message,
          status: error?.response?.status,
        });
        // Soft-fail — don't block app launch
        return {
          matched: false,
          message: error?.message || 'Attribution check failed',
        };
      }
    },

    async claimAttribution(fingerprint, installId) {
      try {
        const payload: {
          device_fingerprint: DeviceFingerprint;
          install_id?: string;
        } = {
          device_fingerprint: fingerprint,
        };
        if (installId) payload.install_id = installId;

        logger.info('[AttributionAPI] Claiming attribution for user', {
          hasInstallId: !!installId,
          platform: fingerprint.platform,
        });

        const response = await http.post<ClaimAttributionResponse>(
          '/attribution/v1/claim',
          payload,
        );

        if (response.data.success) {
          logger.info('[AttributionAPI] Attribution claimed', {
            installId: response.data.install_id,
            campaignCode: response.data.campaign_code,
          });
        } else {
          logger.info('[AttributionAPI] No attribution to claim', {
            message: response.data.message,
          });
        }

        return response.data;
      } catch (error: any) {
        logger.error('[AttributionAPI] Failed to claim attribution', {
          error: error?.message,
          status: error?.response?.status,
        });
        return { success: false, message: error?.message || 'Claim failed' };
      }
    },
  };
}
